// V13 A-2 — lifecycle_stage 한글 라벨 단일 원본.
//
// 왜 모듈로 뺐나: 라벨 맵이 4곳(AptHero · AptHeroLarge · apt/[id] · api/og-apt)에
// 복사돼 있었고 값도 서로 달랐다('분양 예고' vs '분양예정'). 공고 전 단계를 새로
// 다루려면 4곳에 같은 걸 또 붙여야 했다. 원본을 하나로 모은다.
//
// ⚠️ 키는 apt_sites.lifecycle_stage 의 CHECK 허용값과 같아야 한다.
//    2026-08-23 DB 담당이 CHECK 를 확장해 PIPELINE_STAGES 5개가 실제로 저장된다.
//    모르는 값은 null(렌더 생략) — 원문 코드를 화면에 내보내지 않는다(레인 D).

/**
 * 공고 전 파이프라인 5단계. get_apt_pipeline 이 status 로 실어 보낸다.
 * 배열 순서 = 진행 순서(조합설립 → … → 착공). 화면 정렬은 RPC 의 weight 를 따른다.
 *
 * mgmt_approved(관리처분인가)는 V13 원안 4단계에 없었다 — 이주·철거 직전이자
 * 조합원 분양이 확정되는 단계라 실무에서 가장 중요한 분기 중 하나다 (2026-08-23 추가).
 */
export const PIPELINE_STAGES = [
  'union_established',
  'constructor_selected',
  'plan_approved',
  'mgmt_approved',
  'construction',
] as const;

export type PipelineStage = (typeof PIPELINE_STAGES)[number];

/**
 * 「분양예정」 — **아직 모집공고가 안 났다.** 공고 전 정비사업 5단계 + 부지계획 + 분양 예고.
 *
 * ⚠️ 이 집합은 `lead-copy.ts` 의 `pre_notice` 와 «같은 집합» 이다. 거기서 다시 적지 않고
 *    이 상수를 가져다 쓴다 — 어휘를 두 곳에 적으면 한쪽만 늘어난다(STATUS 공리).
 * ⛔ 이 단계에 「분양중」·「분양 개시」를 쓰지 않는다. 분양가도 일정도 아직 없다(§7-1).
 */
export const UPCOMING_SALE_STAGES = [
  ...PIPELINE_STAGES,
  'site_planning',
  'pre_announcement',
] as const;

/**
 * 「분양중」 — **지금 신청할 수 있다.** 접수 중 · 발표 대기 · 발표 · 미분양 잔여세대.
 *
 * ⚠️ `unsold_active`(미분양)를 여기 넣은 것은 «지금 살 수 있다» 는 축이 같아서다.
 *    화면에서 「미분양」이라는 말을 지우려는 것이 아니다 — 라벨은 단계별로 그대로 간다.
 * ⛔ `move_in_*`·`post_move_in` 은 «분양» 이 아니다. 리드폼 대상이라도 여기 넣지 않는다.
 */
export const OPEN_SALE_STAGES = [
  'subscription_open',
  'award_pending',
  'award_announced',
  'unsold_active',
] as const;

/**
 * 단계 → 한글 라벨.
 * 공고 전 5단계 → 청약 라인 → 입주 이후 → 축이 다른 상태 순으로 적는다.
 */
export const LIFECYCLE_LABEL: Record<string, string> = {
  // 공고 전 — V13 A-2
  union_established: '조합설립',
  constructor_selected: '시공사 선정',
  plan_approved: '사업시행인가',
  mgmt_approved: '관리처분인가',
  construction: '착공',

  // 청약 라인
  site_planning: '부지계획',
  pre_announcement: '분양 예고',
  model_house_open: '모델하우스',
  special_supply: '특별공급',
  subscription_open: '청약 진행',
  // H6-1 — 접수 마감 ~ 발표 전. 「청약 진행」으로 두면 «지원할 수 있다» 로 읽힌다.
  award_pending: '당첨자 발표 대기',
  award_announced: '당첨자 발표',
  contract_signing: '계약',
  contract: '계약',

  // 입주 이후
  pre_move_in: '입주 예정',
  // ⚠️ move_in_ready 는 이름과 달리 "입주 준비" 가 아니라 "계약 체결 기간 종료" 다.
  //    실측 720건 전부 입주예정 202609~203104(과거 0건) — 입주는 아직 미래다.
  //    이 단계는 리드폼 노출 대상이라 파워링크 랜딩에도 뜬다. '입주 준비' 로 쓰면
  //    광고 랜딩에서 사실과 다른 말을 하게 된다 (lib/apt/lead-eligibility.ts 참조).
  move_in_ready: '입주 예정',
  move_in: '입주',
  move_in_started: '입주중',
  post_move_in: '입주 후',

  // 축이 다른 상태
  active_trade: '실거래',
  resale: '실거래',
  unsold_active: '미분양',
  landmark_active: '랜드마크',
  redevelopment_active: '재개발',
};

/**
 * 라벨을 돌려준다. 모르는 값은 null — 호출부가 칩·행을 렌더하지 않는다(레인 D G15: 원문 코드 노출 금지).
 * (라벨 맵보다 DB CHECK 가 먼저 늘어나는 상황을 화면이 견뎌야 한다.)
 */
export function lifecycleLabel(stage: string | null | undefined): string | null {
  if (!stage) return null;
  return LIFECYCLE_LABEL[stage] ?? null;
}

export function isPipelineStage(stage: string | null | undefined): stage is PipelineStage {
  return !!stage && (PIPELINE_STAGES as readonly string[]).includes(stage);
}
